import { ECSegmentWebService, RestfulECSegmentWebService } from 'ws/ECSegmentWebService';
import { ALL_VENDOR_OPTION, ECSegment, ECSegmentFormData, ECSegmentListDTO, ECSegmentRule, ECSegmentRulesDetail, ECSegmentStatus, ECSegmentType } from './ECSegment';
import { SelectOptions } from 'components/commonType';
import { DmpWebService, RestfulDmpWebService } from 'ws/DmpWebService';

export interface ECSegmentManager {
  getECSegments (
    advertiserId: number | undefined,
    status?: ECSegmentStatus[]
  ): Promise<ECSegmentListDTO[]>;
  getECSegment (id: string): Promise<ECSegment>;
  createECSegment (segmentData: Partial<ECSegmentFormData>): Promise<void>;
  updateECSegment (
    id: string,
    segmentData: Partial<ECSegmentFormData>
  ): Promise<void>;
  deleteECSegments (ids: string[]): Promise<void>;
  intersectECSegments (
    advertiserId: number,
    name: string,
    id1: string,
    id2: string
  ): Promise<void>;
  getInUsedVendorNumbers (
    agencyId: string | number,
    vendorNumbers: string[]
  ): Promise<SelectOptions[]>;
  getECSegmentRules (id: string, advertiserId: number): Promise<ECSegmentRulesDetail>;
}

export class DefaultECSegmentManager implements ECSegmentManager {
  constructor (
    private webService: ECSegmentWebService = new RestfulECSegmentWebService(),
    private dmpWebService: DmpWebService = new RestfulDmpWebService()
  ) {}

  async getECSegments (
    advertiserId: number | undefined,
    status?: ECSegmentStatus[]
  ): Promise<ECSegmentListDTO[]> {
    return this.webService.getECSegments(
      advertiserId,
      status
    );
  }

  async getECSegment (id: string): Promise<ECSegment> {
    return this.webService.getECSegment(id);
  }

  toServerFormat (segmentData: Partial<ECSegmentFormData>): Partial<ECSegmentFormData> {
    if (segmentData.type === ECSegmentType.PRODUCT_CATEGORY) {
      if (segmentData.vendorNumber === ALL_VENDOR_OPTION.value.toString()) {
        segmentData.vendorNumber = undefined;
      }
    }
    return segmentData;
  }

  async createECSegment (
    segmentData: Partial<ECSegmentFormData>
  ): Promise<void> {
    return this.webService.createECSegment(this.toServerFormat(segmentData));
  }

  async updateECSegment (
    id: string,
    segmentData: Partial<ECSegmentFormData>
  ): Promise<void> {
    return this.webService.updateECSegment(id, this.toServerFormat(segmentData));
  }

  async deleteECSegments (ids: string[]): Promise<void> {
    return this.webService.deleteECSegments(ids);
  }

  async intersectECSegments (
    advertiserId: number,
    name: string,
    id1: string,
    id2: string
  ): Promise<void> {
    return this.webService.intersectECSegments(advertiserId, name, id1, id2);
  }

  async getInUsedVendorNumbers (
    agencyId: string | number,
    vendorNumbers: string[]
  ): Promise<SelectOptions[]> {
    return this.webService.getInUsedVendorNumbers(agencyId, vendorNumbers);
  }

  async getECSegmentRules (id: string, advertiserId: number): Promise<ECSegmentRulesDetail> {
    const ecSegmentRule = await this.webService.getECSegmentRules(id);
    const productRules: ECSegmentRule[] = ecSegmentRule.rules.filter((rule: ECSegmentRule) => rule.type === ECSegmentType.PRODUCT);
    const productIds = productRules.flatMap((rule: ECSegmentRule) => rule.inclusion);
    const productCaches = await this.dmpWebService.getProductCaches(advertiserId, productIds);
    ecSegmentRule.rules = ecSegmentRule.rules.map((rule: ECSegmentRule) => {
      if (rule.type === ECSegmentType.PRODUCT) {
        rule.inclusion = rule.inclusion.map((productId: string) => {
          const productCache = productCaches.find((cache) => cache.productId === productId);
          return productCache ? `${productCache.name} (${productId})` : productId;
        });
      }
      return rule;
    });
    return ecSegmentRule;
  }
}
